import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { RouteModules } from '../../enums/route-modules.enum';
import { JwtLegFiClaims } from '../../services/auth/jwt-legfi-claims.model';
import { GrowlerService } from '../../components/shared/growler/growler.service';
import { TreasuryConversionStageService } from '../../services/treasury/treasury-conversion-stage.service';

@Injectable()
export class PermissionsGuard implements CanActivate, CanActivateChild
{
    constructor(
            private _router: Router,
            private _growler: GrowlerService,
            private _conversionStage: TreasuryConversionStageService,
    ) {
    }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean | UrlTree | Promise<boolean | UrlTree> | Observable<boolean | UrlTree> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();

        // ⚠️ ASKED BEFORE THE FEATURE BLOCK, AND ASKED ASYNCHRONOUSLY (SD-10777, review F-4759
        // and F-76c1). Both halves are load-bearing and each was wrong on its own.
        //
        // BEFORE: the Financial Accounts route carries `feature: 'stripe-treasury.enabled'`, and
        // the feature block below returns early. An organization whose token predates the
        // conversion has no such claim — the claims are built ONCE at token issue — so the menu
        // entry appeared and the route refused, which is precisely the stall this change exists
        // to prevent.
        //
        // ASYNCHRONOUSLY: the stage is fetched over HTTP. Reading it synchronously only ever saw
        // whatever some other component happened to have cached already, so a deep link or a cold
        // load resolved before the answer existed and bounced the one admin who must get through.
        //
        // ⚠️ AND IT CANNOT WIDEN ANYTHING. It admits only an ADMIN, only on the Financial Accounts
        // module, and only while `isConversionInProgressFor()` is true — the two in-flight stages
        // for THIS organization. Everything else falls through to the unchanged checks below.
        if (jwt !== null
            && next.data?.module === RouteModules.FINANCIAL_ACCOUNTS
            && (jwt.admin || jwt.superUser)
        ) {
            return this._conversionStage.fetch(jwt.orgId).pipe(
                map(() => this._conversionStage.isConversionInProgressFor(jwt.orgId)),
                // A failed fetch must not open a route. `fetch()` already degrades to `none`, and
                // this is the belt to that braces.
                catchError(() => of(false)),
                switchMap((inConversion: boolean) => inConversion
                    ? of(true)
                    : from(Promise.resolve(this.decide(next, state, jwt)))),
            );
        }

        return this.decide(next, state, jwt);
    }

    /**
     * The ordinary decision, exactly as it was before SD-10777.
     *
     * Split out rather than rewritten: every organization that is not part-way through a
     * conversion reaches this and nothing about it has changed.
     */
    private decide(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
        jwt: JwtLegFiClaims,
    ): boolean | UrlTree | Promise<boolean | UrlTree> {

        const dashboardView: string[] = [];
        if (LegFiJwtService.featureEnabled('pmc-dashboard.enabled')) {
            dashboardView.push(`pmc/${jwt.pmcId}/dashboard`);
        } else if (jwt?.admin) {
            dashboardView.push('app/organization/dashboard');
        } else {
            dashboardView.push('app/owner/dashboard');
        }
        dashboardView.push(<any>{uri: encodeURIComponent(state.url)});

        if (jwt === null) {
            const loginView = ['/auth/login'];
            loginView.push(<any>{uri: encodeURIComponent(state.url)});
            return this._router.navigate(loginView);
        }

        // no module for permissions specified, all users pass
        if (!next.data.module) {
            return true;
        }

        if (next.data.hasOwnProperty('feature')) {
            // support check for feature enabled or disabled
            const featureCheck = next.data.feature.includes('!')
                ? LegFiJwtService.featureEnabled(next.data.feature)
                : !LegFiJwtService.featureEnabled(next.data.feature);

            if (featureCheck) {
                // allow supers into pmc dashboard
                if (next.data.feature === 'pmc-dashboard.enabled' && jwt.superUser) {
                    return true;
                }

                this._growler.oops('You are not authorized to view the requested feature');
                // redirect if no checks pass
                return this._router.navigate(dashboardView);
            }
        }

        if (next.data.module === RouteModules.SUPER_ADMIN) {
            if (jwt.superUser) {
                return true;
            }
        }

        if (next.data.module === RouteModules.ORG_ADMIN) {
            if (jwt.admin || jwt.superUser) {
                return true;
            }
        }

        const requireWrite = next.data.hasOwnProperty('write') && next.data.write === true;
        if (LegFiJwtService.doesUserHaveModulePermission(next.data.module, requireWrite)) {
            return true;
        }

        this._growler.oops('You are not authorized to view the requested page');
        // redirect if no checks pass
        return this._router.navigate(dashboardView);
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivate(childRoute, state);
    }
}
